import React, { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@redux/hooks';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import { useCachedFetch } from '@hooks/useCachedFetch';
import { useMaintenanceWindow, useUpcomingWindow } from '@shared/hooks/useMaintenanceStatus';
import { ListPageSkeleton, RefreshingIndicator } from '@shared/components/ListLoading';
import { GenericTable } from '@ui-components/Table/GenericTable';
import GenericSearchbar from '@ui-components/Searchbar/GenericSearchbar';
import SegmentedControl from '@ui-components/SegmentedContol/GenericSegmentedContol';
import GenericCard from '@ui-components/Card/GenericCard';
import { buildPredicate, FilterField, ParsedFilter } from 'kontrolEdge/helpers/advancedFilters';
import { normalizeFilters } from 'kontrolEdge/helpers/normalizeFilters';
import { buildPlatformMaintenanceRow } from 'kontrolEdge/helpers/platformMaintenanceRow';
import { getFailoverHistoryAPI, getMonitoringAPI } from '../requests/request';
import { FailoverResponse, FailoverTableRow, MaintenanceResponse, MaintenanceTableRow } from '../OverviewComponents/Types';
import { MaintenanceDetailsModal, OutageDetailsModal } from '../OverviewComponents/OutageDetailsModal';
import {
  makeMaintenanceFields,
  makeOutageFields,
  maintenanceColumns,
  outageColumns,
  toMaintenanceRow,
  toOutageRow,
} from './OutagesData';

type OutagesTab = "outages" | "maintenance";

/** Both tabs come from one load, so they are cached together. */
interface OutagesAndMaintenance {
  outages: FailoverTableRow[];
  maintenance: MaintenanceTableRow[];
}

const NOTHING_RECORDED: OutagesAndMaintenance = { outages: [], maintenance: [] };

const TABS = [
  { label: "Outages", value: "outages" },
  { label: "Maintenance", value: "maintenance" },
];

/** The searchbar's filters applied to one tab's rows, the same engine every Kontrol Edge list uses. */
function useFilteredRows<T>(rows: T[], fields: FilterField<T>[], filters: ParsedFilter[]): T[] {
  return useMemo(() => {
    const normalized = normalizeFilters<T>(filters, fields, {
      defaultStringToContains: true,
      quotedStringsExact: true,
    });
    return rows.filter(buildPredicate<T>(normalized, fields));
  }, [rows, fields, filters]);
}

export default function OutagesView() {
  const api = useAxiosAuth0();
  const location = useLocation();
  const { tenantId, partnerId } = useAppSelector(state => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;

  const [selectedView, setSelectedView] = useState<OutagesTab>(location.state?.selectedView ?? "outages");
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [selectedOutage, setSelectedOutage] = useState<FailoverTableRow | null>(null);
  const [selectedMaintenance, setSelectedMaintenance] = useState<MaintenanceTableRow | null>(null);

  // The platform's own window leads the list, as it does on the Overview: "View all"
  // there must not land on a list that is missing the row it just showed.
  const platformRow = buildPlatformMaintenanceRow(
    useMaintenanceWindow("Kontrol Edge"),
    useUpcomingWindow("Kontrol Edge")
  );

  // Cached per tenant: coming back to the screen paints the last answer at once and
  // refreshes it underneath. A failed load shows no rows, as it always has — the tabs'
  // own "nothing found" states, not an error page.
  const {
    data: { outages, maintenance } = NOTHING_RECORDED,
    isLoading,
    isRefreshing,
  } = useCachedFetch<OutagesAndMaintenance>(
    tenantId ? `kontroledge/outages?tenantId=${tenantId}` : null,
    async () => {
      const [failoverResp, maintenanceResp] = await Promise.all([
        getFailoverHistoryAPI(api, tenantId),
        getMonitoringAPI(api, tenantId),
      ]);
      // Same payloads the Overview reads: `failoverRecords` / `maintenanceRecords`,
      // each record's length in `duration` (seconds).
      const failover: FailoverResponse | undefined = failoverResp?.data?.data;
      const maintenanceData: MaintenanceResponse | undefined = maintenanceResp?.data?.data;
      return {
        outages: Array.isArray(failover?.failoverRecords) ? failover.failoverRecords.map(toOutageRow) : [],
        maintenance: Array.isArray(maintenanceData?.maintenanceRecords)
          ? maintenanceData.maintenanceRecords.map(toMaintenanceRow)
          : [],
      };
    }
  );

  const maintenanceRows = useMemo(
    () => (platformRow ? [platformRow, ...maintenance] : maintenance),
    [platformRow, maintenance]
  );

  const outageFields = useMemo(() => makeOutageFields(isViewingAllCustomers), [isViewingAllCustomers]);
  const maintenanceFields = useMemo(() => makeMaintenanceFields(isViewingAllCustomers), [isViewingAllCustomers]);
  const filteredOutages = useFilteredRows(outages, outageFields, activeFilters);
  const filteredMaintenance = useFilteredRows(maintenanceRows, maintenanceFields, activeFilters);

  const outageTableColumns = useMemo(
    () => outageColumns(isViewingAllCustomers, setSelectedOutage),
    [isViewingAllCustomers]
  );
  const maintenanceTableColumns = useMemo(
    () => maintenanceColumns(isViewingAllCustomers, setSelectedMaintenance),
    [isViewingAllCustomers]
  );

  const isOutagesTab = selectedView === "outages";
  const suggestedKeys = (isOutagesTab ? outageFields : maintenanceFields).map(field => field.label);

  const handleTabChange = (value: string) => {
    // Each tab has its own fields: a filter on "Site" means nothing to maintenance.
    setActiveFilters([]);
    setSelectedView(value as OutagesTab);
  };

  // Nothing to show yet: the page's shape in skeleton, not a screen-wide overlay.
  if (isLoading) {
    return <ListPageSkeleton columns={5} />;
  }

  // Laid out like every Kontrol Edge list (MainDataCenter, MainPort): the table
  // takes the height left under the searchbar instead of a fixed 400px box.
  return (
    <div className="relative flex h-full flex-col overflow-hidden p-4">
      <RefreshingIndicator active={isRefreshing} />
      <div className="relative min-h-0 flex-1">
        <GenericCard
          isCollapsible={false}
          className="flex h-full flex-col"
          headerContent={
            <SegmentedControl options={TABS} value={selectedView} onChange={handleTabChange} size="small" />
          }
          bodyContent={
            <div className="flex h-full min-h-0 flex-col">
              <div className="mb-4 w-full max-w-full flex-shrink-0 sm:max-w-[500px]">
                {/* Keyed by tab so switching clears the chips along with the filters. */}
                <GenericSearchbar
                  key={selectedView}
                  placeholder="Find by attribute or value"
                  suggestedKeys={suggestedKeys}
                  onFiltersChange={setActiveFilters}
                  liveFiltering
                  debounceMs={200}
                />
              </div>
              <div className="flex min-h-0 flex-1 flex-col">
                {isOutagesTab ? (
                  <GenericTable
                    columns={outageTableColumns}
                    data={filteredOutages}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    width="100%"
                    tableClassName="bg-semantic-bg-primary"
                    maxTableBodyHeight="100%"
                  />
                ) : (
                  <GenericTable
                    columns={maintenanceTableColumns}
                    data={filteredMaintenance}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    width="100%"
                    tableClassName="bg-semantic-bg-primary"
                    maxTableBodyHeight="100%"
                  />
                )}
              </div>
            </div>
          }
        />
      </div>

      <OutageDetailsModal outage={selectedOutage} onClose={() => setSelectedOutage(null)} />
      <MaintenanceDetailsModal maintenance={selectedMaintenance} onClose={() => setSelectedMaintenance(null)} />
    </div>
  )
}
